import { useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, FileUp, CheckCircle2, X, Plus, Trash2, FileText,
  Image as ImageIcon, Loader2, Info, User, Users, HeartPulse,
  ArrowLeft, ArrowRight, Check, ChevronDown, Repeat
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const ALLOWED = ["pdf", "jpg", "jpeg", "png"];
const MAX_SIZE = 15 * 1024 * 1024;
const MAX_FILES = 40;

const inputCls = "w-full px-3 py-2.5 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm placeholder:text-[#33436B] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/30";
const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
const uid = () => Math.random().toString(36).slice(2, 9);
const emptyMember = () => ({ name: "", height: "", weight: "", medical_history: "", hospitalization_reason: "", hospitalization_when: "", medication_details: "", files: [] });

const mergeFiles = (existing, list) => {
  const next = [...existing];
  for (const f of Array.from(list || [])) {
    const ext = f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "";
    if (!ALLOWED.includes(ext)) { toast.error(`"${f.name}" skipped — only PDF, JPG, PNG allowed`); continue; }
    if (f.size > MAX_SIZE) { toast.error(`"${f.name}" skipped — keep each file under 15 MB`); continue; }
    if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
    next.push(f);
  }
  return next;
};

const FileList = ({ files, onRemove, testid }) => (
  files.length > 0 && (
    <div className="mt-2 space-y-2" data-testid={testid}>
      {files.map((f, i) => (
        <div key={`${f.name}-${f.size}`} className="flex items-center gap-2 bg-white border border-[#C9D4E6] rounded-xl px-3 py-2">
          {f.type === "application/pdf" ? <FileText className="h-4 w-4 text-red-600 shrink-0" /> : <ImageIcon className="h-4 w-4 text-sky-600 shrink-0" />}
          <span className="text-sm truncate flex-1">{f.name}</span>
          <span className="text-xs text-[#33436B]">{fmtSize(f.size)}</span>
          <button type="button" onClick={() => onRemove(i)} className="text-[#3A4557] hover:text-red-600"><X className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  )
);

/* Upload slot with an explicit button that triggers the file input via ref (reliable across devices) */
const UploadSlot = ({ label, hint, files, onAdd, onRemove, testid }) => {
  const ref = useRef(null);
  return (
    <div className="border border-[#C9D4E6] rounded-xl p-3 bg-white/70">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#1B2A4A]">{label}</p>
          {hint && <p className="text-[11px] text-[#33436B] mt-0.5">{hint}</p>}
        </div>
        <button type="button" onClick={() => ref.current && ref.current.click()}
          className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#D4A017]/15 hover:bg-[#D4A017]/25 text-[#A87608] text-xs font-semibold transition-colors" data-testid={testid}>
          <FileUp className="h-4 w-4" /> {files.length ? "Add more" : "Upload"}
        </button>
      </div>
      <input ref={ref} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden"
        onChange={(e) => { const list = Array.from(e.target.files); onAdd(list); e.target.value = ""; }} data-testid={`${testid}-input`} />
      <FileList files={files} testid={`${testid}-files`} onRemove={onRemove} />
    </div>
  );
};

/* Three medical fields asked for every insured person */
const MedicalFields = ({ data, onChange, prefix }) => (
  <div className="space-y-3">
    <div>
      <label className="text-xs font-medium text-[#2E3B57] mb-1 block">Medical history</label>
      <textarea className={inputCls} rows={2} placeholder="Existing conditions — diabetes, BP, thyroid, heart, etc. Write 'None' if healthy." value={data.medical_history} onChange={(e) => onChange({ medical_history: e.target.value })} data-testid={`${prefix}-medical`} />
    </div>
    <div>
      <label className="text-xs font-medium text-[#2E3B57] mb-1 block">Past hospitalization details</label>
      <textarea className={inputCls} rows={2} placeholder="Reason for hospitalization (write 'None' if never hospitalized)" value={data.hospitalization_reason} onChange={(e) => onChange({ hospitalization_reason: e.target.value })} data-testid={`${prefix}-hosp-reason`} />
      <div className="mt-2">
        <label className="text-[11px] text-[#33436B] mb-1 block">Approx month &amp; year of hospitalization</label>
        <input type="month" className={inputCls} value={data.hospitalization_when} onChange={(e) => onChange({ hospitalization_when: e.target.value })} data-testid={`${prefix}-hosp-when`} />
      </div>
    </div>
    <div>
      <label className="text-xs font-medium text-[#2E3B57] mb-1 block">Current medication details</label>
      <textarea className={inputCls} rows={2} placeholder="Ongoing medicines &amp; dosage. Write 'None' if not applicable." value={data.medication_details} onChange={(e) => onChange({ medication_details: e.target.value })} data-testid={`${prefix}-medication`} />
    </div>
  </div>
);

export default function DocumentCollection() {
  const [stepIdx, setStepIdx] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  const [proposer, setProposer] = useState({ name: "", mobile: "", email: "", height: "", weight: "", medical_history: "", hospitalization_reason: "", hospitalization_when: "", medication_details: "", docs: { pan: [], aadhaar: [], bank: [] } });
  const [proposerInsured, setProposerInsured] = useState(null); // null | true | false
  const [family, setFamily] = useState({ spouse: false, father: false, mother: false, children: [] });
  const [memberData, setMemberData] = useState({});
  const [nominee, setNominee] = useState({ name: "", dob: "", relationship: "" });
  const [portability, setPortability] = useState(null); // null | true | false
  const [portDocs, setPortDocs] = useState({ y1: [], y2: [], y3: [] });
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [summary, setSummary] = useState({ files: 0, name: "" });

  const setDoc = (slot, updater) => setProposer((p) => ({ ...p, docs: { ...p.docs, [slot]: updater(p.docs[slot]) } }));
  const setPort = (slot, updater) => setPortDocs((p) => ({ ...p, [slot]: updater(p[slot]) }));

  const memberOrder = useMemo(() => {
    const arr = [];
    if (family.spouse) arr.push({ key: "spouse", relationship: "Spouse" });
    if (family.father) arr.push({ key: "father", relationship: "Father" });
    if (family.mother) arr.push({ key: "mother", relationship: "Mother" });
    family.children.forEach((c) => arr.push({ key: c.key, relationship: c.relationship }));
    return arr;
  }, [family]);

  const steps = useMemo(() => {
    const s = [{ type: "proposer" }, { type: "proposer_insured" }, { type: "family" }];
    if (proposerInsured === true) s.push({ type: "self" });
    memberOrder.forEach((m) => s.push({ type: "member", key: m.key, relationship: m.relationship }));
    s.push({ type: "portability" });
    s.push({ type: "review" });
    return s;
  }, [proposerInsured, memberOrder]);

  const current = steps[Math.min(stepIdx, steps.length - 1)];
  const md = (key) => memberData[key] || emptyMember();
  const setMd = (key, patch) => setMemberData((prev) => ({ ...prev, [key]: { ...emptyMember(), ...prev[key], ...patch } }));

  const addChild = (rel) => setFamily((f) => ({ ...f, children: [...f.children, { key: uid(), relationship: rel }] }));
  const removeChild = (key) => { setFamily((f) => ({ ...f, children: f.children.filter((c) => c.key !== key) })); setMemberData((p) => { const n = { ...p }; delete n[key]; return n; }); };

  const proposerFileCount = proposer.docs.pan.length + proposer.docs.aadhaar.length + proposer.docs.bank.length;
  const portFileCount = portDocs.y1.length + portDocs.y2.length + portDocs.y3.length;
  const totalFiles = proposerFileCount + memberOrder.reduce((n, m) => n + (md(m.key).files?.length || 0), 0) + (portability === true ? portFileCount : 0);

  const next = () => {
    if (current.type === "proposer") {
      if (!proposer.name.trim()) return toast.error("Please enter the proposer's full name");
      if (proposer.mobile.replace(/\D/g, "").length < 10) return toast.error("Please enter a valid 10-digit mobile number");
    }
    if (current.type === "family" && proposerInsured === false && memberOrder.length === 0) {
      return toast.error("Please select at least one family member to insure");
    }
    if (current.type === "member" && !md(current.key).name.trim()) {
      return toast.error("Please enter this member's full name");
    }
    setStepIdx((i) => Math.min(i + 1, steps.length - 1));
  };
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));
  const chooseInsured = (val) => { setProposerInsured(val); setStepIdx((i) => i + 1); };
  const choosePortability = (val) => {
    setPortability(val);
    if (val === false) { setPortDocs({ y1: [], y2: [], y3: [] }); setStepIdx((i) => Math.min(i + 1, steps.length - 1)); }
  };

  const handleSubmit = () => {
    if (totalFiles === 0) { toast.error("Please attach at least one document"); return; }
    if (totalFiles > MAX_FILES) { toast.error(`Maximum ${MAX_FILES} files`); return; }

    const allMembers = [
      { key: "proposer", name: proposer.name.trim(), relationship: "Self", is_proposer: true, is_insured: proposerInsured === true,
        height: proposerInsured ? proposer.height : "", weight: proposerInsured ? proposer.weight : "", medical_history: proposerInsured ? proposer.medical_history.trim() : "",
        hospitalization_reason: proposerInsured ? proposer.hospitalization_reason.trim() : "", hospitalization_when: proposerInsured ? proposer.hospitalization_when : "", medication_details: proposerInsured ? proposer.medication_details.trim() : "" },
      ...memberOrder.map((m) => { const d = md(m.key); return { key: m.key, name: d.name.trim(), relationship: m.relationship, is_proposer: false, is_insured: true, height: d.height, weight: d.weight, medical_history: d.medical_history.trim(),
        hospitalization_reason: (d.hospitalization_reason || "").trim(), hospitalization_when: d.hospitalization_when || "", medication_details: (d.medication_details || "").trim() }; }),
    ];

    const uploads = [];
    proposer.docs.pan.forEach((f) => uploads.push({ member_key: "proposer", label: "PAN Card", file: f }));
    proposer.docs.aadhaar.forEach((f) => uploads.push({ member_key: "proposer", label: "Aadhaar Card", file: f }));
    proposer.docs.bank.forEach((f) => uploads.push({ member_key: "proposer", label: "Bank Details", file: f }));
    memberOrder.forEach((m) => (md(m.key).files || []).forEach((f) => uploads.push({ member_key: m.key, label: `${m.relationship} document`, file: f })));
    if (portability === true) {
      portDocs.y1.forEach((f) => uploads.push({ member_key: "portability", label: "Previous policy — most recent year", file: f }));
      portDocs.y2.forEach((f) => uploads.push({ member_key: "portability", label: "Previous policy — 2 years ago", file: f }));
      portDocs.y3.forEach((f) => uploads.push({ member_key: "portability", label: "Previous policy — 3 years ago", file: f }));
    }

    setSubmitting(true);
    setProgress(0);
    const fd = new FormData();
    fd.append("name", proposer.name.trim());
    fd.append("mobile", proposer.mobile.trim());
    fd.append("email", proposer.email.trim());
    fd.append("purpose", portability === true ? "Portability" : "Fresh Policy");
    fd.append("is_portability", portability === true ? "true" : "false");
    fd.append("proposer_not_insured", proposerInsured === true ? "false" : "true");
    fd.append("nominee_name", nominee.name.trim());
    fd.append("nominee_dob", nominee.dob.trim());
    fd.append("nominee_relationship", nominee.relationship.trim());
    fd.append("members", JSON.stringify(allMembers));
    const fileMeta = [];
    uploads.forEach((u) => { fd.append("files", u.file, u.file.name); fileMeta.push({ member_key: u.member_key, label: u.label }); });
    fd.append("file_meta", JSON.stringify(fileMeta));

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API}/api/doc-collection/submit`, true);
    xhr.upload.onprogress = (evt) => { if (evt.lengthComputable) setProgress(Math.round((evt.loaded / evt.total) * 100)); };
    xhr.onload = () => {
      setSubmitting(false);
      if (xhr.status >= 200 && xhr.status < 300) { setSummary({ files: totalFiles, name: proposer.name }); setDone(true); }
      else { let msg = "Something went wrong. Please try again."; try { msg = JSON.parse(xhr.responseText).detail || msg; } catch { /* noop */ } toast.error(msg); }
    };
    xhr.onerror = () => { setSubmitting(false); toast.error("Network error. Please try again."); };
    xhr.send(fd);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#F4F6FA] text-[#1B2A4A] flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="doc-thank-you">
          <div className="h-20 w-20 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold mb-3">Documents received, {summary.name.split(" ")[0] || "friend"}! ✅</h1>
          <p className="text-[#33436B]">
            {summary.files} file{summary.files > 1 ? "s" : ""} uploaded successfully. Prashant will use these to fill your proposal form and set up the policy, and will call you on {proposer.mobile} if anything more is needed.
          </p>
        </div>
      </div>
    );
  }

  const stepTitle = {
    proposer: "Proposer details",
    proposer_insured: "Coverage",
    family: "Family members",
    self: "Insured — Yourself",
    member: `Insured — ${current.relationship || ""}`,
    portability: "Existing insurance",
    review: "Review & submit",
  }[current.type];

  return (
    <div className="min-h-screen bg-[#F4F6FA] text-[#1B2A4A]">
      <header className="border-b border-[#C9D4E6] bg-white/60">
        <div className="max-w-xl mx-auto px-4 py-4 flex items-center gap-3">
          <div>
            <img src="/kavachbox-logo-light.png" alt="Kavach Box" className="h-8 w-auto" />
            <p className="text-[9px] text-[#3A4557] mt-0.5">A part of Shree Finserv Group</p>
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs text-[#33436B]">
            <ShieldCheck className="h-4 w-4 text-[#A87608]" /> Secure Upload
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6 pb-28">
        <div className="mb-5" data-testid="wizard-progress">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-[#A87608]">Step {stepIdx + 1} of {steps.length}</span>
            <span className="text-xs text-[#3A4557]">{stepTitle}</span>
          </div>
          <div className="h-1.5 bg-[#EDF1F8] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#D4A017] to-emerald-500 transition-all" style={{ width: `${((stepIdx + 1) / steps.length) * 100}%` }} />
          </div>
        </div>

        {/* ---------- SLIDE: PROPOSER ---------- */}
        {current.type === "proposer" && (
          <div data-testid="slide-proposer" className="space-y-4">
            <div className="bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-2xl p-4 flex gap-3" data-testid="doc-necessary-note">
              <Info className="h-5 w-5 text-[#A87608] shrink-0 mt-0.5" />
              <p className="text-sm text-[#2E3B57]">All the documents requested here are needed to correctly fill the <span className="font-semibold text-[#1B2A4A]">proposal form of your insurance policy</span>. It takes just a few minutes.</p>
            </div>

            <div className="bg-white/60 border border-[#C9D4E6] rounded-2xl p-4">
              <h2 className="text-lg font-bold mb-2 flex items-center gap-2"><User className="h-5 w-5 text-[#A87608]" /> Proposer details</h2>

              {/* Collapsible explainer */}
              <button type="button" onClick={() => setShowInfo((v) => !v)} className="flex items-center gap-1.5 text-xs font-medium text-[#A87608] mb-3" data-testid="proposer-info-toggle">
                <Info className="h-3.5 w-3.5" /> Who is a proposer & why we ask this
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showInfo ? "rotate-180" : ""}`} />
              </button>
              {showInfo && (
                <div className="bg-[#EDF1F8]/60 border border-[#C9D4E6] rounded-xl p-3 mb-4 text-sm text-[#2E3B57]" data-testid="proposer-info">
                  The proposer is the person in whose name the policy is taken and who pays the premium. They receive the <span className="font-semibold">income-tax deduction under Section 80D</span>. The PAN, Aadhaar and bank details below are used to fill the official proposal form.
                </div>
              )}

              <div className="space-y-3">
                <input className={inputCls} placeholder="Proposer's full name *" value={proposer.name} onChange={(e) => setProposer({ ...proposer, name: e.target.value })} data-testid="proposer-name-input" />
                <div className="grid sm:grid-cols-2 gap-3">
                  <input className={inputCls} placeholder="Mobile number *" inputMode="numeric" value={proposer.mobile} onChange={(e) => setProposer({ ...proposer, mobile: e.target.value })} data-testid="proposer-mobile-input" />
                  <input className={inputCls} placeholder="Email ID" type="email" value={proposer.email} onChange={(e) => setProposer({ ...proposer, email: e.target.value })} data-testid="proposer-email-input" />
                </div>
              </div>

              <p className="text-xs font-semibold text-[#1B2A4A] mt-4 mb-2 flex items-center gap-1.5"><FileUp className="h-3.5 w-3.5 text-[#A87608]" /> Upload proposer documents</p>
              <div className="space-y-2.5">
                <UploadSlot label="PAN Card" testid="proposer-doc-pan"
                  files={proposer.docs.pan} onAdd={(l) => setDoc("pan", (x) => mergeFiles(x, l))} onRemove={(i) => setDoc("pan", (x) => x.filter((_, j) => j !== i))} />
                <UploadSlot label="Aadhaar Card" testid="proposer-doc-aadhaar"
                  files={proposer.docs.aadhaar} onAdd={(l) => setDoc("aadhaar", (x) => mergeFiles(x, l))} onRemove={(i) => setDoc("aadhaar", (x) => x.filter((_, j) => j !== i))} />
                <UploadSlot label="Bank Details" hint="Cancelled cheque, passbook front page or bank statement — any one" testid="proposer-doc-bank"
                  files={proposer.docs.bank} onAdd={(l) => setDoc("bank", (x) => mergeFiles(x, l))} onRemove={(i) => setDoc("bank", (x) => x.filter((_, j) => j !== i))} />
              </div>
            </div>
          </div>
        )}

        {/* ---------- SLIDE: IS PROPOSER INSURED ---------- */}
        {current.type === "proposer_insured" && (
          <div data-testid="slide-proposer-insured" className="space-y-4">
            <h2 className="text-lg font-bold">Is the proposer also covered under this policy?</h2>
            <p className="text-sm text-[#33436B]">i.e., will {proposer.name.split(" ")[0] || "the proposer"} also be an insured member?</p>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => chooseInsured(true)} data-testid="proposer-insured-yes"
                className={`rounded-2xl border p-5 text-center transition-all ${proposerInsured === true ? "border-emerald-500 bg-emerald-500/10" : "border-[#C9D4E6] bg-white/60 hover:border-[#D4A017]/50"}`}>
                <CheckCircle2 className="h-7 w-7 mx-auto mb-2 text-emerald-600" />
                <p className="font-semibold text-[#1B2A4A]">Yes, also insured</p>
                <p className="text-[11px] text-[#33436B] mt-1">Self is added as the first insured member</p>
              </button>
              <button onClick={() => chooseInsured(false)} data-testid="proposer-insured-no"
                className={`rounded-2xl border p-5 text-center transition-all ${proposerInsured === false ? "border-[#A87608] bg-[#D4A017]/10" : "border-[#C9D4E6] bg-white/60 hover:border-[#D4A017]/50"}`}>
                <User className="h-7 w-7 mx-auto mb-2 text-[#A87608]" />
                <p className="font-semibold text-[#1B2A4A]">No, payer only</p>
                <p className="text-[11px] text-[#33436B] mt-1">Proposer pays but is not covered</p>
              </button>
            </div>
          </div>
        )}

        {/* ---------- SLIDE: FAMILY SELECTION ---------- */}
        {current.type === "family" && (
          <div data-testid="slide-family" className="space-y-4">
            <h2 className="text-lg font-bold flex items-center gap-2"><Users className="h-5 w-5 text-[#A87608]" /> Who else do you want to insure?</h2>
            {proposerInsured === true && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 text-sm text-emerald-800" data-testid="self-included-note">
                <span className="font-semibold">{proposer.name || "You"} (Self)</span> is already included as the first insured member.
              </div>
            )}
            <p className="text-sm text-[#33436B]">Select the family members to add to this policy:</p>
            <div className="flex flex-wrap gap-2">
              {[["spouse", "Spouse"], ["father", "Father"], ["mother", "Mother"]].map(([k, label]) => (
                <button key={k} onClick={() => setFamily((f) => ({ ...f, [k]: !f[k] }))} data-testid={`family-${k}`}
                  className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${family[k] ? "border-emerald-500 bg-emerald-500/10 text-emerald-700" : "border-[#C9D4E6] bg-white/60 text-[#2E3B57] hover:border-[#D4A017]/50"}`}>
                  {family[k] && <Check className="h-3.5 w-3.5 inline mr-1" />}{label}
                </button>
              ))}
            </div>

            <div className="bg-white/60 border border-[#C9D4E6] rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-[#1B2A4A]">Children</p>
                <div className="flex gap-2">
                  <button onClick={() => addChild("Son")} data-testid="add-son-btn" className="flex items-center gap-1 text-xs font-medium text-[#A87608] hover:underline"><Plus className="h-3.5 w-3.5" /> Son</button>
                  <button onClick={() => addChild("Daughter")} data-testid="add-daughter-btn" className="flex items-center gap-1 text-xs font-medium text-[#A87608] hover:underline"><Plus className="h-3.5 w-3.5" /> Daughter</button>
                </div>
              </div>
              {family.children.length === 0 ? (
                <p className="text-xs text-[#33436B]">No children added. Tap “Son” or “Daughter” to add one by one.</p>
              ) : (
                <div className="space-y-1.5">
                  {family.children.map((c, i) => (
                    <div key={c.key} className="flex items-center justify-between bg-[#EDF1F8]/50 rounded-lg px-3 py-2" data-testid={`child-row-${i}`}>
                      <span className="text-sm text-[#1B2A4A]">{c.relationship} {family.children.filter((x) => x.relationship === c.relationship).indexOf(c) + 1}</span>
                      <button onClick={() => removeChild(c.key)} className="text-[#3A4557] hover:text-red-600" data-testid={`remove-child-${i}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---------- SLIDE: SELF ---------- */}
        {current.type === "self" && (
          <div data-testid="slide-self" className="space-y-4">
            <h2 className="text-lg font-bold">Your details (Self)</h2>
            <div className="bg-white/60 border border-[#C9D4E6] rounded-2xl p-4 space-y-3">
              <div>
                <label className="text-xs text-[#3A4557]">Name (from proposer)</label>
                <input className={`${inputCls} bg-[#EDF1F8]/60`} value={proposer.name} readOnly data-testid="self-name-readonly" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input className={inputCls} placeholder="Height (cm)" inputMode="numeric" value={proposer.height} onChange={(e) => setProposer({ ...proposer, height: e.target.value })} data-testid="self-height" />
                <input className={inputCls} placeholder="Weight (kg)" inputMode="numeric" value={proposer.weight} onChange={(e) => setProposer({ ...proposer, weight: e.target.value })} data-testid="self-weight" />
              </div>
              <MedicalFields data={proposer} onChange={(patch) => setProposer((p) => ({ ...p, ...patch }))} prefix="self" />
              <p className="text-[11px] text-[#33436B] flex items-center gap-1"><Check className="h-3.5 w-3.5 text-emerald-600" /> Your documents are already collected in the proposer step.</p>
            </div>
          </div>
        )}

        {/* ---------- SLIDE: MEMBER ---------- */}
        {current.type === "member" && (
          <div data-testid="slide-member" className="space-y-4">
            <h2 className="text-lg font-bold">{current.relationship} details</h2>
            <div className="bg-white/60 border border-[#C9D4E6] rounded-2xl p-4 space-y-3">
              <input className={inputCls} placeholder={`${current.relationship}'s full name *`} value={md(current.key).name} onChange={(e) => setMd(current.key, { name: e.target.value })} data-testid="member-name" />
              <div className="grid grid-cols-2 gap-3">
                <input className={inputCls} placeholder="Height (cm)" inputMode="numeric" value={md(current.key).height} onChange={(e) => setMd(current.key, { height: e.target.value })} data-testid="member-height" />
                <input className={inputCls} placeholder="Weight (kg)" inputMode="numeric" value={md(current.key).weight} onChange={(e) => setMd(current.key, { weight: e.target.value })} data-testid="member-weight" />
              </div>
              <MedicalFields data={md(current.key)} onChange={(patch) => setMd(current.key, patch)} prefix="member" />
              <UploadSlot label="Documents" hint="Aadhaar Card (Birth Certificate if a child)" testid="member-dropzone"
                files={md(current.key).files} onAdd={(l) => setMd(current.key, { files: mergeFiles(md(current.key).files, l) })} onRemove={(i) => setMd(current.key, { files: md(current.key).files.filter((_, j) => j !== i) })} />
            </div>
          </div>
        )}

        {/* ---------- SLIDE: PORTABILITY ---------- */}
        {current.type === "portability" && (
          <div data-testid="slide-portability" className="space-y-4">
            <h2 className="text-lg font-bold flex items-center gap-2"><Repeat className="h-5 w-5 text-[#A87608]" /> Are you switching from an existing insurance?</h2>
            <p className="text-sm text-[#33436B]">
              If you're moving (porting) from another health insurance policy to this new one, sharing your previous policy copies helps carry forward your waiting-period and no-claim benefits.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => choosePortability(true)} data-testid="portability-yes"
                className={`rounded-2xl border p-5 text-center transition-all ${portability === true ? "border-emerald-500 bg-emerald-500/10" : "border-[#C9D4E6] bg-white/60 hover:border-[#D4A017]/50"}`}>
                <Repeat className="h-7 w-7 mx-auto mb-2 text-emerald-600" />
                <p className="font-semibold text-[#1B2A4A]">Yes, I'm porting</p>
                <p className="text-[11px] text-[#33436B] mt-1">Switching from an existing policy</p>
              </button>
              <button onClick={() => choosePortability(false)} data-testid="portability-no"
                className={`rounded-2xl border p-5 text-center transition-all ${portability === false ? "border-[#A87608] bg-[#D4A017]/10" : "border-[#C9D4E6] bg-white/60 hover:border-[#D4A017]/50"}`}>
                <CheckCircle2 className="h-7 w-7 mx-auto mb-2 text-[#A87608]" />
                <p className="font-semibold text-[#1B2A4A]">No, it's a fresh policy</p>
                <p className="text-[11px] text-[#33436B] mt-1">No previous insurance</p>
              </button>
            </div>

            {portability === true && (
              <div className="bg-white/60 border border-[#C9D4E6] rounded-2xl p-4 space-y-3" data-testid="portability-uploads">
                <div className="bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl p-3 flex gap-2.5">
                  <Info className="h-4 w-4 text-[#A87608] shrink-0 mt-0.5" />
                  <p className="text-[13px] text-[#2E3B57]">
                    Upload your <span className="font-semibold text-[#1B2A4A]">previous policy copies for the last 3 years</span>. This is <span className="font-semibold">optional</span> — even one or two years is perfectly fine.
                  </p>
                </div>
                <UploadSlot label="Most recent year's policy" hint="Your latest / current policy copy"
                  testid="port-doc-y1" files={portDocs.y1} onAdd={(l) => setPort("y1", (x) => mergeFiles(x, l))} onRemove={(i) => setPort("y1", (x) => x.filter((_, j) => j !== i))} />
                <UploadSlot label="Previous year's policy" hint="Policy for the year before"
                  testid="port-doc-y2" files={portDocs.y2} onAdd={(l) => setPort("y2", (x) => mergeFiles(x, l))} onRemove={(i) => setPort("y2", (x) => x.filter((_, j) => j !== i))} />
                <UploadSlot label="Year before that" hint="Third-year-back policy (if available)"
                  testid="port-doc-y3" files={portDocs.y3} onAdd={(l) => setPort("y3", (x) => mergeFiles(x, l))} onRemove={(i) => setPort("y3", (x) => x.filter((_, j) => j !== i))} />
              </div>
            )}
          </div>
        )}

        {/* ---------- SLIDE: REVIEW ---------- */}
        {current.type === "review" && (
          <div data-testid="slide-review" className="space-y-4">
            <h2 className="text-lg font-bold">Review & submit</h2>
            <div className="bg-white/60 border border-[#C9D4E6] rounded-2xl p-4 space-y-2">
              {[{ name: proposer.name, relationship: "Self", is_proposer: true, is_insured: proposerInsured === true, count: proposerFileCount }, ...memberOrder.map((m) => ({ name: md(m.key).name, relationship: m.relationship, is_proposer: false, is_insured: true, count: (md(m.key).files || []).length }))].map((m, i) => (
                <div key={i} className="flex items-center gap-2 flex-wrap text-sm" data-testid={`review-member-${i}`}>
                  <span className="font-semibold text-[#1B2A4A]">{m.name || m.relationship}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#EDF1F8] text-[#2E3B57]">{m.relationship}</span>
                  {m.is_proposer && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#D4A017]/20 text-[#A87608] font-semibold">Proposer</span>}
                  {m.is_insured ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700">Insured</span> : <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#EDF1F8] text-[#3A4557]">Payer only</span>}
                  <span className="ml-auto text-xs text-[#33436B]">{m.count} file{m.count === 1 ? "" : "s"}</span>
                </div>
              ))}
            </div>

            {portability !== null && (
              <div className="bg-white/60 border border-[#C9D4E6] rounded-2xl p-4 flex items-center gap-3" data-testid="review-portability">
                <Repeat className={`h-5 w-5 shrink-0 ${portability ? "text-emerald-600" : "text-[#A87608]"}`} />
                <p className="text-sm text-[#2E3B57]">
                  {portability
                    ? <>Porting from an existing policy · <span className="font-semibold text-[#1B2A4A]">{portFileCount} previous polic{portFileCount === 1 ? "y" : "ies"}</span> attached</>
                    : <>Fresh policy — not switching from another insurer</>}
                </p>
              </div>
            )}

            <div className="bg-white/60 border border-[#C9D4E6] rounded-2xl p-4">
              <h3 className="text-sm font-semibold text-[#A87608] mb-3 flex items-center gap-2"><HeartPulse className="h-4 w-4" /> Nominee details (optional)</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                <input className={inputCls} placeholder="Nominee name" value={nominee.name} onChange={(e) => setNominee({ ...nominee, name: e.target.value })} data-testid="nominee-name-input" />
                <input className={inputCls} placeholder="Date of birth" type="text" onFocus={(e) => (e.target.type = "date")} value={nominee.dob} onChange={(e) => setNominee({ ...nominee, dob: e.target.value })} data-testid="nominee-dob-input" />
                <input className={inputCls} placeholder="Relationship" value={nominee.relationship} onChange={(e) => setNominee({ ...nominee, relationship: e.target.value })} data-testid="nominee-relationship-input" />
              </div>
            </div>

            {submitting && (
              <div>
                <div className="h-2 bg-[#EDF1F8] rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#D4A017] to-emerald-500 transition-all" style={{ width: `${progress}%` }} /></div>
                <p className="text-xs text-[#33436B] mt-1 text-center">Uploading… {progress}%</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Sticky footer nav */}
      <div className="fixed bottom-0 inset-x-0 border-t border-[#C9D4E6] bg-white/90 backdrop-blur">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-3">
          {stepIdx > 0 && (
            <button onClick={back} disabled={submitting} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-[#C9D4E6] text-[#2E3B57] text-sm font-medium disabled:opacity-50" data-testid="wizard-back-btn">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          )}
          {current.type === "review" ? (
            <button onClick={handleSubmit} disabled={submitting} className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] text-[#0a1628] font-bold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="submit-docs-btn">
              {submitting ? (<><Loader2 className="h-5 w-5 animate-spin" /> Uploading…</>) : (<><FileUp className="h-5 w-5" /> Submit ({totalFiles} file{totalFiles === 1 ? "" : "s"})</>)}
            </button>
          ) : current.type === "proposer_insured" || (current.type === "portability" && portability === null) ? (
            <p className="flex-1 text-center text-xs text-[#33436B]">Choose an option above to continue</p>
          ) : (
            <button onClick={next} className="flex-1 py-3 rounded-xl bg-[#1B2A4A] text-white font-semibold flex items-center justify-center gap-2" data-testid="wizard-next-btn">
              Next <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
